import type { BrowserDisplaySession, BrowserViewport } from "@cmmd-forge/contracts";
import {
  capturePointToViewportPoint,
  type BrowserCanvasPoint,
  type BrowserCaptureLayout,
} from "./BrowserCanvas.logic";

export type BrowserKeyboardCommand =
  | { readonly type: "pressKey"; readonly key: string }
  | { readonly type: "typeText"; readonly text: string };

export const REMOTE_WEBRTC_BROWSER_CHROME_OFFSET_Y = 0;

export const RECENT_BROWSER_KEYDOWN_DEDUP_MS = 150;
export const ACTIVE_BROWSER_KEYDOWN_DEDUP_MS = 5_000;

function browserDisplayChromeOffset(
  display: BrowserDisplaySession | undefined,
): BrowserCanvasPoint {
  return display?.transport === "remote-webrtc" ? { x: 0, y: 0 } : { x: 0, y: 0 };
}

export function resolveInteractiveBrowserSurfaceLayout(input: {
  readonly containerWidth: number;
  readonly containerHeight: number;
  readonly viewport: BrowserViewport | undefined;
  readonly display: BrowserDisplaySession | undefined;
}): BrowserCaptureLayout | null {
  if (input.display?.transport !== "remote-webrtc") {
    return null;
  }
  const viewport = input.viewport;
  if (
    !viewport ||
    input.containerWidth <= 0 ||
    input.containerHeight <= 0 ||
    viewport.width <= 0 ||
    viewport.height <= 0
  ) {
    return null;
  }

  return {
    left: 0,
    top: 0,
    width: input.containerWidth,
    height: input.containerHeight,
    scale: Math.min(input.containerWidth / viewport.width, input.containerHeight / viewport.height),
    scaleX: input.containerWidth / viewport.width,
    scaleY: input.containerHeight / viewport.height,
  };
}

export function mapBrowserPointerToViewport(input: {
  readonly point: BrowserCanvasPoint;
  readonly layout: BrowserCaptureLayout | null;
  readonly viewport: BrowserViewport | undefined;
  readonly display: BrowserDisplaySession | undefined;
}): BrowserCanvasPoint {
  const offset = browserDisplayChromeOffset(input.display);
  const adjustedPoint = {
    x: Math.max(0, input.point.x - offset.x),
    y: Math.max(0, input.point.y - offset.y),
  };
  if (input.layout && input.viewport) {
    return capturePointToViewportPoint({
      point: adjustedPoint,
      layout: input.layout,
      viewport: input.viewport,
    });
  }
  return adjustedPoint;
}

export function resolveBrowserDisplayEmptyLabel(input: {
  readonly display: BrowserDisplaySession | undefined;
  readonly fallback: string;
}): string {
  switch (input.display?.status) {
    case "connected":
    case "connecting":
    case "reconnecting":
      return input.fallback;
    case "degraded":
      return input.display.detail ?? "Browser display is degraded.";
    case "failed":
      return input.display.detail ?? "Browser display failed.";
    case "unsupported":
      return input.display.detail ?? "Browser display is not supported in this environment.";
    case undefined:
      return input.fallback;
  }
}

export function shouldShowInteractiveBrowserEmptyState(input: {
  readonly display: BrowserDisplaySession | undefined;
}): boolean {
  return !(
    input.display?.transport === "remote-webrtc" &&
    (input.display.status === "connected" || input.display.status === "connecting")
  );
}

export function isInteractiveBrowserScrollCaptureActive(input: {
  readonly interactionEnabled: boolean;
  readonly selectionEnabled: boolean;
  readonly tabId: string | null;
  readonly display: BrowserDisplaySession | undefined;
}): boolean {
  return (
    input.interactionEnabled &&
    !input.selectionEnabled &&
    Boolean(input.tabId) &&
    input.display?.transport === "remote-webrtc" &&
    (input.display.status === "connected" ||
      input.display.status === "connecting" ||
      input.display.status === "reconnecting")
  );
}

export function shouldRouteInteractiveBrowserFocusedKey(input: {
  readonly surfaceContainsTarget: boolean;
  readonly hasKeyboardOwnership: boolean;
  readonly activeElementIsSurfaceOrDescendant: boolean;
  readonly activeElementIsDocumentRoot: boolean;
}): boolean {
  if (input.surfaceContainsTarget) {
    return true;
  }
  if (!input.hasKeyboardOwnership) {
    return false;
  }
  return input.activeElementIsSurfaceOrDescendant || input.activeElementIsDocumentRoot;
}

export function shouldDeduplicateBrowserPointerFallback(input: {
  readonly source: "click" | "mouse" | "pointer";
  readonly button: number;
  readonly clientX: number;
  readonly clientY: number;
  readonly recent: {
    readonly button: number;
    readonly clientX: number;
    readonly clientY: number;
    readonly at: number;
  } | null;
  readonly now: number;
}): boolean {
  return (
    input.source !== "pointer" &&
    input.recent !== null &&
    input.recent.button === input.button &&
    input.recent.clientX === input.clientX &&
    input.recent.clientY === input.clientY &&
    input.now - input.recent.at < RECENT_BROWSER_KEYDOWN_DEDUP_MS
  );
}

export function resolveBrowserKeyboardDispatch(input: {
  readonly commandSignature: string;
  readonly eventType: "keydown" | "keypress" | "keyup" | "input";
  readonly now: number;
  readonly activeKeyDown: {
    readonly signature: string;
    readonly at: number;
  } | null;
  readonly recentKeyDown: {
    readonly signature: string;
    readonly at: number;
  } | null;
}): {
  readonly shouldSend: boolean;
  readonly shouldConsume: boolean;
  readonly nextActiveKeyDown: {
    readonly signature: string;
    readonly at: number;
  } | null;
  readonly nextRecentKeyDown: {
    readonly signature: string;
    readonly at: number;
  } | null;
} {
  if (
    input.eventType === "keyup" &&
    input.activeKeyDown?.signature === input.commandSignature &&
    input.now - input.activeKeyDown.at < ACTIVE_BROWSER_KEYDOWN_DEDUP_MS
  ) {
    return {
      shouldSend: false,
      shouldConsume: true,
      nextActiveKeyDown: null,
      nextRecentKeyDown: input.recentKeyDown,
    };
  }

  if (
    input.eventType !== "keydown" &&
    input.recentKeyDown?.signature === input.commandSignature &&
    input.now - input.recentKeyDown.at < RECENT_BROWSER_KEYDOWN_DEDUP_MS
  ) {
    return {
      shouldSend: false,
      shouldConsume: true,
      nextActiveKeyDown: input.activeKeyDown,
      nextRecentKeyDown: input.recentKeyDown,
    };
  }

  if (input.eventType === "keydown") {
    const keyDown = { signature: input.commandSignature, at: input.now };
    return {
      shouldSend: true,
      shouldConsume: true,
      nextActiveKeyDown: keyDown,
      nextRecentKeyDown: keyDown,
    };
  }

  return {
    shouldSend: true,
    shouldConsume: true,
    nextActiveKeyDown: input.activeKeyDown,
    nextRecentKeyDown: input.recentKeyDown,
  };
}

const BROWSER_CONTROL_KEYS = new Set([
  "Enter",
  " ",
  "Space",
  "Spacebar",
  "Backspace",
  "Tab",
  "Escape",
  "Delete",
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "ArrowDown",
  "Home",
  "End",
  "PageUp",
  "PageDown",
]);

export function mapBrowserKeyboardEvent(input: {
  readonly key: string;
  readonly code?: string;
  readonly metaKey?: boolean;
  readonly ctrlKey?: boolean;
  readonly altKey?: boolean;
  readonly shiftKey?: boolean;
}): BrowserKeyboardCommand | null {
  if (input.metaKey || input.ctrlKey || input.altKey) {
    return null;
  }
  const normalizedKey = input.key.toLowerCase();
  if (
    input.key === " " ||
    normalizedKey === "spacebar" ||
    normalizedKey === "space" ||
    input.code === "Space"
  ) {
    return { type: "pressKey", key: "Space" };
  }
  if (input.key.length === 1) {
    return { type: "typeText", text: input.key };
  }
  if (BROWSER_CONTROL_KEYS.has(input.key)) {
    return { type: "pressKey", key: input.key };
  }
  return null;
}
